import React, { useRef, useState, useEffect } from 'react';
import { GeneratePromoCodePayload } from '../promoter.service';
import { convertLocalDateToJsDate } from 'src/utils/date.util';
import { TopUpType } from '../types/promo-code.entity';

interface AssignPromoCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: GeneratePromoCodePayload) => void;
  promoterId: string;
}

export const AssignPromoCodeModal: React.FC<AssignPromoCodeModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  promoterId
}) => {
  const [formData, setFormData] = useState({
    code: '',
    bonusPercentage: 10,
    usageLimit: '',
    minimumPurchaseAmount: '',
    expiryDate: '',
    active: true,
    appliedTo: [] as TopUpType[]
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  
  const promoCodeInputRef = useRef<HTMLInputElement>(null);
  const bonusPercentageSliderRef = useRef<HTMLInputElement>(null);
  const usageLimitInputRef = useRef<HTMLInputElement>(null);
  const minimumPurchaseInputRef = useRef<HTMLInputElement>(null);
  const expiryDateInputRef = useRef<HTMLInputElement>(null);
  const activeCheckboxRef = useRef<HTMLInputElement>(null);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        code: '',
        bonusPercentage: 10,
        usageLimit: '',
        minimumPurchaseAmount: '',
        expiryDate: '',
        active: true,
        appliedTo: []
      });
      setErrors({});
    }
  }, [isOpen]);

  // Focus first input when modal opens
  useEffect(() => {
    if (isOpen && promoCodeInputRef.current) {
      promoCodeInputRef.current.focus();
    }
  }, [isOpen]);

  const handleInputChange = (field: string, value: string | number | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleTopUpTypeToggle = (type: TopUpType) => {
    setFormData(prev => ({
      ...prev,
      appliedTo: prev.appliedTo.includes(type)
        ? prev.appliedTo.filter(t => t !== type)
        : [...prev.appliedTo, type]
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.code.trim()) {
      newErrors.code = 'Promo code is required';
    }

    if (formData.bonusPercentage < 0 || formData.bonusPercentage > 100) {
      newErrors.bonusPercentage = 'Bonus percentage must be between 0 and 100';
    }

    if (!formData.expiryDate) {
      newErrors.expiryDate = 'Expiry date is required';
    } else {
      const expiry = new Date(formData.expiryDate);
      if (expiry <= new Date()) {
        newErrors.expiryDate = 'Expiry date must be in the future';
      }
    }

    if (formData.appliedTo.length === 0) {
      newErrors.appliedTo = 'Please select at least one top-up type';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onConfirm({
        code: formData.code.trim(),
        promoterId,
        bonusPercentage: formData.bonusPercentage,
        active: formData.active,
        usageLimit: formData.usageLimit ? parseInt(formData.usageLimit) : undefined,
        expiryDate: convertLocalDateToJsDate(formData.expiryDate)!,
        minimumPurchaseAmount: formData.minimumPurchaseAmount ? parseFloat(formData.minimumPurchaseAmount) : undefined,
        appliedTo: formData.appliedTo.length > 0 ? formData.appliedTo : undefined
      });
    }
  };

  const handleCancel = () => {
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4">
          <h2 className="text-xl font-bold text-white">Assign Promo Code</h2>
        </div>

        {/* Form Content */}
        <div className="p-6 space-y-6">
          {/* Promo Code Input */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">
              <svg className="inline-block w-4 h-4 mr-2 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
              </svg>
              Promo Code *
            </label>
            <div className="relative">
              <input
                ref={promoCodeInputRef}
                type="text"
                value={formData.code}
                onChange={(e) => handleInputChange('code', e.target.value)}
                maxLength={20}
                className={`w-full pl-12 pr-4 py-3 border-2 rounded-xl text-base font-semibold transition-all focus:ring-4 focus:ring-blue-100 focus:border-blue-500 ${
                  errors.code ? 'border-red-500' : 'border-gray-200'
                }`}
                placeholder="Enter promo code"
                style={{ textTransform: 'none' }}
              />
              <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
            <p className="mt-2 text-xs text-gray-500">Enter a unique promo code (max 20 characters)</p>
            {errors.code && (
              <p className="mt-1 text-sm text-red-600">{errors.code}</p>
            )}
          </div>

          {/* Bonus Percentage Slider */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">
              <svg className="inline-block w-4 h-4 mr-2 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 4a3 3 0 00-3 3v6a3 3 0 003 3h10a3 3 0 003-3V7a3 3 0 00-3-3H5zm-1 9v-1h5v2H5a1 1 0 01-1-1zm7 1h4a1 1 0 001-1v-1h-5v2zm0-4h5V8h-5v2zM9 8H4v2h5V8z" clipRule="evenodd" />
              </svg>
              Bonus Percentage (%) *
            </label>
            
            {/* Slider */}
            <div className="mb-4">
              <input
                ref={bonusPercentageSliderRef}
                type="range"
                min="0"
                max="100"
                step="1"
                value={formData.bonusPercentage}
                onChange={(e) => handleInputChange('bonusPercentage', parseInt(e.target.value))}
                className="w-full h-4 bg-gray-200 rounded-lg appearance-none cursor-pointer range-slider"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-2">
                <span>0%</span>
                <span>25%</span>
                <span>50%</span>
                <span>75%</span>
                <span>100%</span>
              </div>
            </div>
            
            {/* Bonus Percentage Display */}
            <div className="text-center">
              <div className="inline-flex items-center p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border-2 border-blue-200 shadow-lg">
                <div className="w-9 h-9 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl mr-3 flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 4a3 3 0 00-3 3v6a3 3 0 003 3h10a3 3 0 003-3V7a3 3 0 00-3-3H5zm-1 9v-1h5v2H5a1 1 0 01-1-1zm7 1h4a1 1 0 001-1v-1h-5v2zm0-4h5V8h-5v2zM9 8H4v2h5V8z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <div className="text-xl font-bold text-blue-700">
                    {formData.bonusPercentage}%
                  </div>
                  <div className="text-xs text-blue-600 font-medium">Bonus Percentage</div>
                </div>
              </div>
            </div>
            
            {errors.bonusPercentage && (
              <p className="mt-2 text-sm text-red-600 text-center">{errors.bonusPercentage}</p>
            )}
          </div>

          {/* Usage Limit Input */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">
              <svg className="inline-block w-4 h-4 mr-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
              </svg>
              Usage Limit
            </label>
            <div className="relative">
              <input
                ref={usageLimitInputRef}
                type="number"
                min="1"
                value={formData.usageLimit}
                onChange={(e) => handleInputChange('usageLimit', e.target.value)}
                className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl text-base font-medium transition-all focus:ring-4 focus:ring-gray-100 focus:border-gray-500"
                placeholder="Maximum usage count"
              />
              <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
            <p className="mt-2 text-xs text-gray-500">Maximum number of times this code can be used (optional)</p>
          </div>

          {/* Minimum Purchase Amount Input */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">
              <svg className="inline-block w-4 h-4 mr-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 000 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                <circle cx="10" cy="10" r="6" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.3"/>
                <circle cx="10" cy="10" r="2" fill="currentColor" opacity="0.6"/>
              </svg>
              Minimum Purchase
            </label>
            <div className="relative">
              <input
                ref={minimumPurchaseInputRef}
                type="number"
                min="0"
                step="0.01"
                value={formData.minimumPurchaseAmount}
                onChange={(e) => handleInputChange('minimumPurchaseAmount', e.target.value)}
                className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl text-base font-medium transition-all focus:ring-4 focus:ring-gray-100 focus:border-gray-500"
                placeholder="Minimum order amount"
              />
              <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 000 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                  <circle cx="10" cy="10" r="6" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.3"/>
                  <circle cx="10" cy="10" r="2" fill="currentColor" opacity="0.6"/>
                </svg>
              </div>
            </div>
            <p className="mt-2 text-xs text-gray-500">Minimum order amount required to use this code (optional)</p>
          </div>

          {/* Enhanced Expiry Date Input */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">
              <svg className="inline-block w-4 h-4 mr-2 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
              </svg>
              Expiry Date *
            </label>
            <div className="relative">
              <input
                ref={expiryDateInputRef}
                type="date"
                min={new Date().toISOString().split('T')[0]}
                value={formData.expiryDate}
                onChange={(e) => handleInputChange('expiryDate', e.target.value)}
                className={`w-full pl-16 pr-4 py-4 border-2 rounded-2xl text-base font-semibold transition-all focus:ring-4 focus:ring-blue-100 focus:border-blue-500 ${
                  errors.expiryDate ? 'border-red-500' : 'border-gray-200'
                }`}
                style={{ color: '#1e40af' }}
              />
              <div className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-gradient-to-r from-blue-500 to-indigo-600 p-2 rounded-lg shadow-lg">
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                </svg>
              </div>
              {/* Calendar Enhancement Overlay */}
              <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <div className="flex items-center gap-1 text-blue-500 text-xs font-semibold">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                  </svg>
                  <span>Select Date</span>
                </div>
              </div>
            </div>
            <div className="mt-2 flex items-center gap-2 text-xs text-blue-500 font-medium">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              When this promo code will expire (minimum: today)
            </div>
            {errors.expiryDate && (
              <p className="mt-1 text-sm text-red-600">{errors.expiryDate}</p>
            )}
          </div>

          {/* Active Status Toggle */}
          <div>
            <label className="flex items-center p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl cursor-pointer transition-all hover:shadow-md">
              <input
                ref={activeCheckboxRef}
                type="checkbox"
                checked={formData.active}
                onChange={(e) => handleInputChange('active', e.target.checked)}
                className="w-5 h-5 mr-3 text-blue-500 rounded focus:ring-2 focus:ring-blue-500"
              />
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Active (Enable this promo code)</span>
              </div>
            </label>
          </div>

          {/* Top-Up Type Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">
              <svg className="inline-block w-4 h-4 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
              </svg>
              Apply To Top-Up Types
            </label>
            <div className="space-y-3">
              <div className="flex items-center p-3 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl cursor-pointer transition-all hover:shadow-md hover:border-green-300"
                   onClick={() => handleTopUpTypeToggle('KARMA')}>
                <input
                  type="checkbox"
                  checked={formData.appliedTo.includes('KARMA')}
                  onChange={() => {}} // Handled by onClick
                  className="w-5 h-5 mr-3 text-green-500 rounded focus:ring-2 focus:ring-green-500"
                />
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg mr-3 flex items-center justify-center">
                    <span className="text-white font-bold text-sm">K</span>
                  </div>
                  <div>
                    <span className="text-sm font-semibold text-gray-700">KARMA</span>
                    <p className="text-xs text-gray-500">Virtual currency for in-app purchases</p>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center p-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl cursor-pointer transition-all hover:shadow-md hover:border-blue-300"
                   onClick={() => handleTopUpTypeToggle('EUR')}>
                <input
                  type="checkbox"
                  checked={formData.appliedTo.includes('EUR')}
                  onChange={() => {}} // Handled by onClick
                  className="w-5 h-5 mr-3 text-blue-500 rounded focus:ring-2 focus:ring-blue-500"
                />
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg mr-3 flex items-center justify-center">
                    <span className="text-white font-bold text-sm">€</span>
                  </div>
                  <div>
                    <span className="text-sm font-semibold text-gray-700">EUR</span>
                    <p className="text-xs text-gray-500">Real currency for premium features</p>
                  </div>
                </div>
              </div>
            </div>
            <p className="mt-2 text-xs text-gray-500">Select which top-up types this promo code applies to (required)</p>
            {errors.appliedTo && (
              <p className="mt-1 text-sm text-red-600">{errors.appliedTo}</p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-gray-500 focus:border-gray-500 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all transform hover:scale-105"
          >
            Generate Promo Code
          </button>
        </div>
      </div>
    </div>
  );
};
